import type { Handler, Match } from "navigo";
import { modal, router, setPageContent } from "..";
import {
	delFarmcamSim,
	postFarmcamSimData,
	putFarmcamSim,
} from "../api/farmcam-sims_{imsi}";
import { PriceWrapper, prices } from "../billing/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/remove_device_modal";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { t } from "../locale";
import { removeNotificationListener } from "../mobile";
import { state } from "../state";
import { formatDataAmount } from "../utils";
import { Bulma } from "../view/bulma";
import { centeredBox } from "../view/components";
import { Html } from "../view/html";

const loadFarmcamSimSettingsPage: Handler = async (ctx: Match | undefined) => {
	if (!ctx?.data?.imsi) throw "missing ctx.data.imsi";
	const imsi = ctx.data.imsi;
	const fcs = state.farmcamSims[imsi];

	const user = await state.getActiveUser();
	const isOwner = user.Email === fcs.Metadata.OwnerEmail;

	const {
		FarmcamSimSmall,
		FarmcamSimMedium,
		FarmcamSimLarge,
		FarmcamSimExtraData,
	} = await prices;

	const subscription = subscriptionField(
		fcs.Subscription,
		[
			new PriceWrapper(user.Country),
			new PriceWrapper(user.Country, FarmcamSimSmall),
			new PriceWrapper(user.Country, FarmcamSimMedium),
			new PriceWrapper(user.Country, FarmcamSimLarge),
		],
		isOwner,
	);
	const sharing = sharingField(fcs.Metadata);
	const tags = tagsField(fcs.Metadata);
	const name = nameField(fcs.Metadata.Name);
	const owner = ownerField(fcs.Metadata, isOwner);

	const onClickSave = (): void => {
		const attempt = errorSection.attempt(async () => {
			const fcs = await putFarmcamSim(imsi, {
				PriceId: subscription.getPriceId(),
				Name: name.getName(),
				Access: sharing.getAccess(),
				Notify: sharing.getNotify(),
				Tags: tags.getTags(),
				Owner: owner.getOwner(),
			});
			state.farmcamSims[fcs.Imsi] = fcs;
			router.jump(`/devices?id=${fcs.Imsi}`);
		});
		loadingScreen.waitFor(attempt);
	};

	const unregister = (): void => {
		const attempt = errorSection.attempt(async () => {
			await delFarmcamSim(fcs.Imsi);
			delete state.farmcamSims[fcs.Imsi];
			removeNotificationListener("farmcamsim", fcs.Imsi);
			router.jump("/devices");
		});
		loadingScreen.waitFor(attempt);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FarmCam SIM ${fcs.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMSI: ${fcs.Imsi}`, "is-6"),
			buyExtraDataField(
				ctx,
				fcs,
				new PriceWrapper(user.Country, FarmcamSimExtraData),
			),
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			Bulma.buttons(
				Bulma.button(
					{
						onClick: onClickSave,
						styles: ["is-primary"],
					},
					t.save,
				),
				Bulma.button(
					{
						styles: ["is-primary"],
						onClick: () => router.jump(`/devices?id=${fcs.Imsi}`),
					},
					t.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () => openUnregisterDeviceModal(unregister),
						styles: ["is-danger"],
					},
					t.remove,
				),
			),
		),
	);
};

const buyExtraDataField = (
	ctx: Match,
	fcs: FarmcamSim,
	price: PriceWrapper,
): Node => {
	const onClickBuyExtraData = async (): Promise<void> => {
		modal.open({
			header: t.farmcamSim.settingsPage.confirmExtraDataPurchaseHeader,
			body: [
				Html.paragraph(
					t.farmcamSim.settingsPage.extraDataPurchaseDisclaimer(
						price.toString(),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: onClickConfirm,
						styles: ["is-primary"],
					},
					t.confirm,
				),
			],
		});
	};

	const onClickConfirm = async (): Promise<void> => {
		const attempt = errorSection.attempt(async () => {
			await postFarmcamSimData(fcs.Imsi);
			loadFarmcamSimSettingsPage(ctx);
		});
		loadingScreen.waitFor(attempt);
	};

	return Bulma.field(
		{},
		Bulma.label(
			`${t.farmcamSim.remainingDataLabel}: ${formatDataAmount(fcs.RemainingData)}`,
		),
		Bulma.control(
			{},
			Bulma.button(
				{
					disabled: !fcs.Subscription.PriceIds.Current,
					onClick: onClickBuyExtraData,
					styles: ["is-primary"],
				},
				t.farmcamSim.settingsPage.buyExtraDataButton,
			),
		),
	);
};

export { loadFarmcamSimSettingsPage };
